/**
 * Модальне вікно реєстрації користувачів
 * Підтримує реєстрацію через email, телефон та Google OAuth
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { USER_ROLES, RegisterData } from '../../types/auth';
import PhoneInput from '../../components/PhoneInput';
import './style.css';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLogin?: () => void;
}

type RegistrationType = 'email' | 'phone';

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose, onOpenLogin }) => {
  const { register, loginWithGoogle } = useAuth();
  const { language } = useLanguage();
  
  // Стан форми
  const [registrationType, setRegistrationType] = useState<RegistrationType>('phone');
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    phone: '',
    password: '',
    first_name: '',
    last_name: '',
    role: USER_ROLES.CLIENT
  });
  const [countryCode, setCountryCode] = useState('+380');
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  
  // Стан завантаження та помилок
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(true);

  // Скидання форми
  const resetForm = () => {
    setFormData({
      email: '',
      phone: '',
      password: '',
      first_name: '',
      last_name: '',
      role: USER_ROLES.CLIENT
    });
    setError(null);
    setIsLoading(false);
  };

  // Закриття модального вікна
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Зміна типу реєстрації
  const handleRegistrationTypeChange = (type: RegistrationType) => {
    setRegistrationType(type);
    setError(null);
    // Очищаємо відповідні поля
    if (type === 'email') {
      setFormData((prev: RegisterData) => ({ ...prev, phone: '' }));
    } else {
      setFormData((prev: RegisterData) => ({ ...prev, email: '' }));
    }
  };

  // Обробка змін в формі
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: RegisterData) => ({ ...prev, [name]: value }));
    setError(null);
  };

  // Валідація форми
  const validateForm = (): string | null => {
    if (!formData.first_name.trim()) {
      return language === 'ua' ? 'Введіть ім\'я' : 'Enter first name';
    }
    
    if (!formData.last_name.trim()) {
      return language === 'ua' ? 'Введіть прізвище' : 'Enter last name';
    }
    
    if (!formData.password || formData.password.length < 6) {
      return language === 'ua' ? 'Пароль повинен містити мінімум 6 символів' : 'Password must be at least 6 characters';
    }
    
    if (registrationType === 'email') {
      if (!formData.email || !formData.email.includes('@')) {
        return language === 'ua' ? 'Введіть правильний email' : 'Enter valid email';
      }
    } else {
      if (!formData.phone || formData.phone.length < 10) {
        return language === 'ua' ? 'Введіть правильний номер телефону' : 'Enter valid phone number';
      }
    }
    
    return null;
  };

  // Обробка телефону з розділенням коду країни
  const handlePhoneInputChange = (fullPhone: string, code: string, localNumber: string) => {
    setCountryCode(code);
    setLocalPhoneNumber(localNumber);
    setFormData((prev: RegisterData) => ({ ...prev, phone: fullPhone }));
    setError(null);
  };

  // Відправка форми
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Підготовка даних для відправки
      const registerData: RegisterData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        role: formData.role
      };
      
      // Додаємо email або телефон в залежності від типу реєстрації
      if (registrationType === 'email') {
        registerData.email = formData.email?.trim();
      } else {
        registerData.phone = formData.phone?.trim();
      }
      
      await register(registerData);
      
      // Успішна реєстрація
      handleClose();
      
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || (language === 'ua' ? 'Помилка реєстрації' : 'Registration error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Google OAuth
  const handleGoogleSignUp = async () => {
    try {
      setIsLoading(true);
      await loginWithGoogle();
    } catch (err: any) {
      console.error('Google signup error:', err);
      setError(err.message || (language === 'ua' ? 'Помилка Google реєстрації' : 'Google signup error'));
    } finally {
      setIsLoading(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="register-modal-backdrop">
      <div className="register-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose}>
          ✕
        </button>
        
        <div className="modal-header">
          <i className="modal-icon bi bi-person-plus"></i>
          <h2>{language === 'ua' ? 'Створити акаунт' : 'Create Account'}</h2>
          <p>{language === 'ua' ? 'Заповніть дані для реєстрації' : 'Fill in your details to get started'}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="register-form">
          {/* Перемикач типу реєстрації */}
          <div className="registration-type-selector">
            <button
              type="button"
              className={`type-button ${registrationType === 'phone' ? 'active' : ''}`}
              onClick={() => handleRegistrationTypeChange('phone')}
            >
              <i className="bi bi-phone"></i>
              {language === 'ua' ? 'Телефон' : 'Phone'}
            </button>
            <button
              type="button"
              className={`type-button ${registrationType === 'email' ? 'active' : ''}`}
              onClick={() => handleRegistrationTypeChange('email')}
            >
              <i className="bi bi-envelope"></i>
              Email
            </button>
          </div>

          {/* Помилка */}
          {error && (
            <div className="error-message">
              <i className="bi bi-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {/* Ім'я та прізвище */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                <i className="bi bi-person"></i>
                {language === 'ua' ? 'Ім\'я' : 'First Name'}
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder={language === 'ua' ? 'Ваше ім\'я' : 'Your first name'}
                disabled={isLoading}
                required
                autoComplete="given-name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="last_name">
                <i className="bi bi-person"></i>
                {language === 'ua' ? 'Прізвище' : 'Last Name'}
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder={language === 'ua' ? 'Ваше прізвище' : 'Your last name'}
                disabled={isLoading}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          {/* Email або телефон */}
          {registrationType === 'email' ? (
            <div className="form-group full-width">
              <label htmlFor="email">
                <i className="bi bi-envelope"></i>
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                disabled={isLoading}
                required
                autoComplete="email"
              />
            </div>
          ) : (
            <div className="form-group full-width">
              <label htmlFor="phone">
                <i className="bi bi-phone"></i>
                {language === 'ua' ? 'Номер телефону' : 'Phone Number'}
              </label>
              <PhoneInput
                countryCode={countryCode}
                localNumber={localPhoneNumber}
                onChange={handlePhoneInputChange}
                placeholder={language === 'ua' ? 'XX XXX XX XX' : 'XX XXX XX XX'}
                disabled={isLoading}
                required
              />
            </div>
          )}

          {/* Пароль */}
          <div className="form-group full-width">
            <label htmlFor="password">
              <i className="bi bi-lock"></i>
              {language === 'ua' ? 'Пароль' : 'Password'}
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={language === 'ua' ? 'Мінімум 6 символів' : 'Minimum 6 characters'}
                disabled={isLoading}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
          </div>

          {/* Угоди та умови */}
          <div className="form-footer">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
              <span>
                Продовжуючи, я підтверджую, що згоден (-на) з{' '}
                <a href="/public-offer" target="_blank" rel="noopener noreferrer" className="link">
                  Умовами та Правилами
                </a>
                , й ознайомлений (-на) із{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link">
                  Повідомленням про обробку персональних даних
                </a>
              </span>
            </label>
          </div>

          {/* Кнопка реєстрації */}
          <button
            type="submit"
            className="submit-btn"
            disabled={isLoading || !acceptTerms}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                {language === 'ua' ? 'Реєстрація...' : 'Signing up...'}
              </>
            ) : (
              <>
                <i className="bi bi-person-plus"></i>
                {language === 'ua' ? 'Зареєструватися' : 'Create Account'}
              </>
            )}
          </button>

          {/* OAuth separator */}
          <div className="oauth-separator">
            {language === 'ua' ? 'або' : 'or'}
          </div>

          {/* OAuth buttons */}
          <div className="oauth-buttons">
            <button
              type="button"
              className="google-btn"
              onClick={handleGoogleSignUp}
              disabled={isLoading || !acceptTerms}
            >
              <svg className="google-icon" width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === 'ua' ? 'Продовжити з Google' : 'Continue with Google'}
            </button>
          </div>

          {/* Посилання на логін */}
          <div className="login-link">
            {language === 'ua' ? 'Вже маєте акаунт?' : 'Already have an account?'}
            <button 
              type="button"
              onClick={(e) => { 
                e.preventDefault(); 
                handleClose(); 
                onOpenLogin?.(); 
              }}
              className="login-link-btn"
            >
              {language === 'ua' ? 'Увійти' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterModal;
